import { Logger } from "@nolimitcity/slot-launcher/bin/utils/Logger";
import { Presentation } from "../../../core/gameflow/controller/Presentation";
import { GameData } from "../../server/data/GameData";
import { SanEngineManager } from "../../engine/SanEngineManager";
import { RevealerManager } from "../../modules/revealer/RevealerManager";
import { SoundManager } from "../../config/sound/SoundManager";
import { SoundConfig } from "../../config/sound/SoundConfig";
import { ReavealerState, Revealer } from "../../modules/revealer/Revealer";
import { GameConfig, SanGameMode } from "../../config/base/GameConfig";
import { StateHandler } from "../../../core/utils/StateHandler";
import { ScatterRevealPresentation } from "../features/scatter/ScatterRevealPresentation";
import { SpinePlayer } from "../../../core/displayobject/SpinePlayer";
import { SanQuentin2 } from "../../SanQuentin2";
import { Sequence } from "../../../core/displayobject/Sequence";
import { BLEND_MODES } from "pixi.js";
import { Helper } from "../../../core/utils/Helper";

export enum ReelStoppedFlow {
    DOUBLEXWAYS,
    EXTRAFS,
    DOUBLE,
    CONVERT,
    SHOW,
    SPLIT,
    REVEAL,
    EXPAND,
    MYSTERYREV
}

export class ReelStoppedPresentation extends Presentation {

    protected reelRevealIndex: number = 0;
    protected flowState: StateHandler = new StateHandler(ReelStoppedFlow.CONVERT, new Map<Number, Function>([
        [ReelStoppedFlow.CONVERT, () => this.convertScatters()],
        [ReelStoppedFlow.REVEAL, () => this.revealPres()],
        [ReelStoppedFlow.SHOW, () => this.barPresentation()],
        [ReelStoppedFlow.DOUBLEXWAYS, () => this.doDoubleXways()],
        [ReelStoppedFlow.SPLIT, () => this.extraSplit()],
        [ReelStoppedFlow.EXPAND, () => this.expandXWays()],
        [ReelStoppedFlow.MYSTERYREV, () => this.revealMyst()],
    ]));

    protected speedUpOnMiss:boolean = false;
    protected ignoreBlackness:boolean = false;

    protected skipped:boolean = false;
    protected showCounter:number = 0;

    public setGameData(data: GameData): void {
        super.setGameData(data);
        this.speedUpOnMiss = !data.Win.singleWin;
        this.ignoreBlackness = !data.Win.CreateNearWinMap()[0].find(v => v);
    }

    public checkForActivation(data: any): boolean {
        return data.nextMode !== SanGameMode.FREESPIN_SETUP1 && !!data.Reel.amountOfScatters;
    }

    public preStart(): void {
        this.flowState.set(ReelStoppedFlow.CONVERT);
        this.reelRevealIndex = -1;
        this.showCounter = 0;
        this.skipped = false;
    }

    // Show Top & Bottom. If Split, splitt directly.
    // If XWays, wait until end. Apply Attention to them.
    public createPresentation(skip:Function) {
        this.flowState.triggerState();
        if(!this.skipped) skip(true);
        else{ 
            this.timeline.add(() => {
                super.skip();
            },0)
        }
    }

    protected revealMyst(): void {
        let playSound = false;
        let isDouble = false;

        SanEngineManager.RAll(r => {
            if(!playSound){
                playSound = RevealerManager.getTopBar(r.reelID).isExpanded || RevealerManager.getBotBar(r.reelID).isExpanded;
            }

            if(playSound && !isDouble){
                isDouble = RevealerManager.getTopBar(r.reelID).isExpanded && RevealerManager.getBotBar(r.reelID).isExpanded;
            }
                
            this.timeline.add(RevealerManager.getTopBar(r.reelID).expandRes(), 0);
            this.timeline.add(RevealerManager.getBotBar(r.reelID).expandRes(), 0);
        });

        if(playSound && !isDouble){
            playSound = false;
            SoundManager.playSoundEffect( SoundConfig.XWAY_REVEAL_SINGLE);
        }

        this.setPlayable(false);
    }

    protected revealPres(): void {

        if(this.ignoreBlackness)
            SanEngineManager.RSWindow((rc, sym) => {
                sym.markNonWin(false, undefined, 0.25);
            })

        for (let i = this.reelRevealIndex + 1; i < this._gameData.raw.topBottomReelOpened.length; i++) {
            if (this._gameData.raw.topBottomReelOpened[i]) {
                this.showCounter++;
                this.reelRevealIndex = i;
                this.timeline.add(ScatterRevealPresentation.revealBar(i), 0);
                if(this.showCounter == this._gameData.Reel.amountOfScatters)
                    SoundManager.stopSoundEffect(SoundConfig.REELS_SPINNING,400); 
                this.flowState.set(ReelStoppedFlow.DOUBLEXWAYS);
                return;
            }
        }

        this.flowState.set(ReelStoppedFlow.SPLIT);
    }

    public doDoubleXways(): void {
        // let playOnce:boolean = true;

        if (this._gameData.raw.topBottomReelOpened[this.reelRevealIndex]) {
            const revTop: Revealer = RevealerManager.getTopBar(this.reelRevealIndex);
            const revBott: Revealer = RevealerManager.getBotBar(this.reelRevealIndex);

            if (revTop.state.equal(ReavealerState.EXPANDING) && revBott.state.equal(ReavealerState.EXPANDING)) {
                this.timeline.add(ScatterRevealPresentation.revealTwoMyst(this.reelRevealIndex), 0);
            }
        }

        this.flowState.set(ReelStoppedFlow.SHOW);
    }

    public extraSplit(): void {

        let playLineFireSound = true;
        if(this._gameData.Reel.specialSplits.length){
            let delay = 0;
            let reelSplitIndx = -1;
            let reelRowSplitIndx:number[] = [];
            const wildSplitAnimDurr = SpinePlayer.fetchDuration("Wild","split");
            SanEngineManager.RSWindow((rc, sym) => {
                if (this._gameData.Reel.specialSplits[rc.reelID][sym.posID[0]]) {
                    if(reelSplitIndx != rc.reelID && !reelRowSplitIndx.find(v => v == sym.posID[0])  && sym.wildExtraSplit){
                        const item = SanQuentin2.overlay.playStandAloneSequence(SanQuentin2.assetLoader.getTextures("lineSplit"));
                        (item.item as Sequence).blendMode = BLEND_MODES.ADD;
                        item.item.position = Helper.copyLocalPosition(item.item, SanEngineManager.getReel(2));
                        item.item.position.y = Helper.copyLocalPosition(item.item, sym).y;
                        item.item.zIndex = 400;
                        item.item.scale.set(2.5);
                        item.item.visible = false;
                        if(rc.reelID > 2) item.item.scale.x *=-1;
                        reelSplitIndx = rc.reelID;
                        reelRowSplitIndx.push(sym.posID[0]);
                        item.timeline.pause();
                        this.timeline.add([() => { item.timeline.play();item.item.visible = true;}], wildSplitAnimDurr);
                        this.timeline.to(this,{duration:item.timeline.duration()/2},wildSplitAnimDurr);
                        if(playLineFireSound){
                            playLineFireSound = false;
                            if(sym.isJumpWild) SoundManager.playSoundEffect(SoundConfig.MULT_UPGRADE);
                            SoundManager.playSoundEffect(SoundConfig.BONUS_WILD_SPLIT);
                        }
                    }
                    const splitSize = this._gameData.Reel.symbolSizeMap[rc.reelID][sym.posID[0]+1];
                    this.timeline.add(sym.MarkedForExtraSplit(false)!, delay);
                    if(sym.splitAmm != splitSize)
                        this.timeline.add(sym.doSplit(2, true, splitSize), wildSplitAnimDurr);
                }
            })
        }
        this.flowState.set(ReelStoppedFlow.EXPAND);
    }

    public barPresentation(): void {

        const revTop: Revealer = RevealerManager.getTopBar(this.reelRevealIndex);
        const revBott: Revealer = RevealerManager.getBotBar(this.reelRevealIndex);
        let playSoundsplits: boolean = false;

        // Store state checks in variables to avoid redundant method calls
        const isRevTopNormal = revTop.state.equal(ReavealerState.NORMAL);
        const isRevBottNormal = revBott.state.equal(ReavealerState.NORMAL);
        const isRevTopSplitter = revTop.state.equal(ReavealerState.SPLITTER);
        const isRevBottSplitter = revBott.state.equal(ReavealerState.SPLITTER);

        if (isRevTopNormal && isRevBottNormal) {
            this.timeline.to(this, { duration: 0.05 }, 0);
        } else if (isRevTopSplitter && isRevBottSplitter) {
            const splitOffset: number = GameConfig.SPLIT_OFFSET;

            revTop.glowMult();
            revBott.glowMult();

            const reelSymbols = SanEngineManager.getReel(this.reelRevealIndex).AllWindowSymbols;
            const scatterPosition = this._gameData.Reel.scatterPositions[this.reelRevealIndex];

            reelSymbols.forEach((s, i) => {
                const tt = (i % 2) * splitOffset;
                if (scatterPosition == s.posID[0] && !s.isJumpWild) {
                    this.timeline.add(s.MarkedForExtraSplit(true, 2), tt);
                } else {
                    this.timeline.add(s.doSplit(4), tt);
                }
            });
            playSoundsplits = true;

        } else {
            let soundCheck = false;
            soundCheck = this.playFeaturePerBar(revBott,this.reelRevealIndex);
            playSoundsplits = !playSoundsplits ? soundCheck : false;
            soundCheck = this.playFeaturePerBar(revTop,this.reelRevealIndex);
            playSoundsplits = !playSoundsplits ? soundCheck : false;
        }

        if (playSoundsplits) {
            SoundManager.playSoundEffect(SoundConfig.WILD_SPLIT);
        }

        this.flowState.set(ReelStoppedFlow.REVEAL);
    }

    protected expandXWays(): void {
        SanQuentin2.xWaysCounter.setValue(undefined,this._gameData.raw.totalWays,0.25);

       let soundExpands: boolean = false;
        this._gameData.raw.topBottomReelOpened.forEach((val, reelIndx) => {
            if (val) {
                const revTop = RevealerManager.getTopBar(reelIndx);
                const revBott = RevealerManager.getBotBar(reelIndx);
                triggerExpand(revBott, this.timeline);
                triggerExpand(revTop, this.timeline);
            }
        })

        function triggerExpand(rev: Revealer, tl: gsap.core.Timeline) {
            if (rev.state.equal(ReavealerState.EXPANDING) && !rev.isExpanded) {
                soundExpands = true;
                tl.add(rev.expand(true), 0);
            }
        }

        if (soundExpands) SoundManager.playSoundSkipEffect(SoundConfig.XWAYS_SINGLE,SoundConfig.SKIP_XWAYS_BASEGAME,this.skipped);
        this.flowState.set(ReelStoppedFlow.MYSTERYREV);
    }

    protected playFeaturePerBar(rev: Revealer,id:number): boolean {
        if (rev.state.equal(ReavealerState.SPLITTER)) {
            rev.glowMult();
            this.timeline.add(ScatterRevealPresentation.doReelSplit(rev, id), 0);
            return true;
        }
        return false;
    }

    protected convertScatters(): void {
        SoundManager.playSoundSkipEffect(SoundConfig.SCATTER_TO_WILD,SoundConfig.SKIP_SCATTER_BASEGAME,this.skipped);

        const flashDurr: number = 0.3;
        const openDurr: number = SpinePlayer.fetchDuration("S_normal", "transform");
        let scatterWildDurr: number = 0;

        SanEngineManager.RSWindow((rc, sym) => {
            if (sym.isScatter) {
                rc.convertAnim();
                scatterWildDurr = sym.onScatterToWild(false,this.skipped).duration();
                this.timeline.add(() => {
                    RevealerManager.getTopBar(rc.reelID).openBar(true);
                    RevealerManager.getBotBar(rc.reelID).openBar(true);
                }, openDurr*0.75);
            }else{
                if(this.ignoreBlackness)
                    sym.markNonWin(true, undefined, flashDurr);
            } 
        })

        this.timeline.to(this, { duration: scatterWildDurr-0.4 }, 0);
        this.flowState.set(ReelStoppedFlow.REVEAL);
    }

    public skip(): void {
        this.skipped = true;
        SoundConfig.SKIP_KILL_BASE.forEach(sound => {
            SoundManager.stopSoundEffect(sound);
        })
        SanEngineManager.RSWindow((reel,sym) => {
            sym.onSkipped();
        })
        super.skip();
    }
    public done(): void {
        Logger.logDev("Stopped Done");
    }
}